import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { baseApi } from "@/store/baseApi";

// Create an API endpoint for handling Thumbtack OAuth
const thumbtackApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    exchangeThumbtackCode: builder.mutation<
      { success: boolean },
      { code: string; state: string }
    >({
      query: (data) => ({
        url: "integrations/thumbtack/auth",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const { useExchangeThumbtackCodeMutation } = thumbtackApi;

const ThumbtackOauth = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // @ts-expect-error - TODO: fix this
  const [exchangeCode, { isLoading, isError, error }] =
    useExchangeThumbtackCodeMutation();
  const [status, setStatus] = useState<"processing" | "success" | "error">(
    "processing"
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const handleOAuthRedirect = async () => {
      // Parse query parameters
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const state = params.get("state");

      // Validate that we have the required parameters
      if (!code || !state) {
        setStatus("error");
        setErrorMessage("Missing required OAuth parameters");
        return;
      }

      try {
        // Exchange the code for an access token via your backend
        await exchangeCode({ code, state }).unwrap();
        setStatus("success");

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate("/");
        }, 3000);
      } catch (err) {
        console.error("OAuth exchange failed:", err);
        setStatus("error");
        setErrorMessage(
          err instanceof Error
            ? err.message
            : "Failed to complete Thumbtack integration"
        );
      }
    };

    handleOAuthRedirect();
  }, [location, exchangeCode, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-center mb-6">
          Thumbtack Integration
        </h1>

        {status === "processing" && (
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
            <p className="mt-4">Connecting your Thumbtack account...</p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center text-green-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mx-auto text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <p className="mt-4 text-lg font-medium">
              Thumbtack account connected successfully!
            </p>
            <p className="mt-2 text-sm text-gray-600">
              Redirecting you back to the dashboard...
            </p>
          </div>
        )}

        {status === "error" && (
          <div className="text-center text-red-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mx-auto text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <p className="mt-4 text-lg font-medium">
              Failed to connect Thumbtack account
            </p>
            <p className="mt-2 text-sm text-gray-600">
              {errorMessage || "An unknown error occurred"}
            </p>
            <button
              onClick={() => navigate("/")}
              className="mt-6 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThumbtackOauth;
